import type { Database } from '@trade-connector/shared';

export type ServiceCaseRow = Database['public']['Tables']['service_cases']['Row'];
export type ServiceCaseEventRow = Database['public']['Tables']['service_case_events']['Row'];
export type ServiceCasePhase = Database['public']['Enums']['service_case_phase'];
export type ServiceCaseOutcome = Database['public']['Enums']['service_case_outcome'];
export type ServiceCaseLabourClaimStatus =
    Database['public']['Enums']['service_case_labour_claim_status'];
export type RegistrationCoverageRow =
    Database['public']['Functions']['compute_registration_coverage']['Returns'][number];

export type CoverageDecision = 'PENDING' | 'COVERED' | 'NOT_COVERED' | 'GOODWILL';
export type ServiceCaseAnchorKind = 'job' | 'registration';

export interface ServiceCaseRegistrationEmbed {
    id: string;
    serial_number: string;
    model_text: string | null;
    site_address: string | null;
    site_suburb: string | null;
    site_state: string | null;
    site_postcode: string | null;
    installed_on: string | null;
    status: string;
    homeowner_name: string | null;
    manufacturer_id: string;
    manufacturers: {
        id: string;
        display_name: string;
        logo_url: string | null;
        /** ACCREDITED_FIRST | OPEN_MARKET; seeds the "Book a visit" dispatch mode. */
        default_dispatch_mode?: string | null;
    } | null;
    manufacturer_models: { id: string; model_code: string } | null;
}

/** A `service_cases` row with the PostgREST embeds in `SERVICE_CASE_SELECT`. */
export interface ServiceCaseWithRelations extends ServiceCaseRow {
    customer: { id: string; company_name: string | null } | null;
    original_job: {
        id: string;
        title: string | null;
        site_suburb: string | null;
        site_state: string | null;
    } | null;
    trade: { id: string; company_name: string | null } | null;
    registration: ServiceCaseRegistrationEmbed | null;
    warranty_claim: { id: string; claim_number: string | null; claim_status: string | null } | null;
}

/**
 * Embeds use explicit FK hints because `service_cases` has three FKs to `jobs`.
 * `manufacturer_id` / `model_id` carry no FK, so names for job-anchored cases are
 * resolved separately (see `useManufacturerOptions`).
 */
export const SERVICE_CASE_SELECT = `
    *,
    customer:customers!service_cases_customer_id_fkey(id, company_name),
    original_job:jobs!service_cases_original_job_id_fkey(id, title, site_suburb, site_state),
    trade:trade_companies!service_cases_trade_company_id_fkey(id, company_name),
    registration:install_registrations!service_cases_registration_id_fkey(
        id, serial_number, model_text, site_address, site_suburb, site_state, site_postcode,
        installed_on, status, homeowner_name, manufacturer_id,
        manufacturers(id, display_name, logo_url, default_dispatch_mode),
        manufacturer_models(id, model_code)
    ),
    warranty_claim:warranty_claims!service_cases_warranty_claim_id_fkey(id, claim_number, claim_status)
`;

export const SERVICE_CASE_PHASES: readonly ServiceCasePhase[] = [
    'TRIAGE',
    'ATTENDANCE',
    'WARRANTY',
    'REPLACEMENT',
    'VERIFICATION',
    'CLOSED',
];

export const SERVICE_CASE_PHASE_LABELS: Record<ServiceCasePhase, string> = {
    TRIAGE: 'Triage',
    ATTENDANCE: 'Attendance',
    WARRANTY: 'Warranty',
    REPLACEMENT: 'Replacement',
    VERIFICATION: 'Verification',
    CLOSED: 'Closed',
};

export const SERVICE_CASE_OUTCOMES: readonly ServiceCaseOutcome[] = [
    'RESOLVED_NO_FAULT',
    'RESOLVED_COMMS',
    'REPLACED_UNDER_WARRANTY',
    'REPLACED_PAID',
    'NOT_A_PRODUCT_FAULT',
    'ABANDONED',
];

export const SERVICE_CASE_OUTCOME_LABELS: Record<ServiceCaseOutcome, string> = {
    RESOLVED_NO_FAULT: 'Resolved — no fault found',
    RESOLVED_COMMS: 'Resolved — communications',
    REPLACED_UNDER_WARRANTY: 'Replaced under warranty',
    REPLACED_PAID: 'Replaced — paid',
    NOT_A_PRODUCT_FAULT: 'Not a product fault',
    ABANDONED: 'Abandoned',
};

export const LABOUR_CLAIM_STATUSES: readonly ServiceCaseLabourClaimStatus[] = [
    'NOT_STARTED',
    'SUBMITTED',
    'APPROVED',
    'REJECTED',
    'NOT_APPLICABLE',
];

export const LABOUR_CLAIM_LABELS: Record<ServiceCaseLabourClaimStatus, string> = {
    NOT_STARTED: 'Not started',
    SUBMITTED: 'Submitted',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    NOT_APPLICABLE: 'Not applicable',
};

export const COVERAGE_DECISIONS: readonly CoverageDecision[] = [
    'PENDING',
    'COVERED',
    'NOT_COVERED',
    'GOODWILL',
];

export const COVERAGE_DECISION_LABELS: Record<CoverageDecision, string> = {
    PENDING: 'Pending',
    COVERED: 'Covered',
    NOT_COVERED: 'Not covered',
    GOODWILL: 'Goodwill',
};

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export function phaseChipColor(phase: ServiceCasePhase): ChipColor {
    switch (phase) {
        case 'TRIAGE':
            return 'warning';
        case 'ATTENDANCE':
        case 'WARRANTY':
        case 'REPLACEMENT':
            return 'info';
        case 'VERIFICATION':
            return 'primary';
        case 'CLOSED':
            return 'success';
        default:
            return 'default';
    }
}

export function coverageChipColor(decision: string): ChipColor {
    switch (decision) {
        case 'COVERED':
            return 'success';
        case 'GOODWILL':
            return 'info';
        case 'NOT_COVERED':
            return 'error';
        default:
            return 'warning';
    }
}

export function labourClaimChipColor(status: ServiceCaseLabourClaimStatus): ChipColor {
    switch (status) {
        case 'APPROVED':
            return 'success';
        case 'REJECTED':
            return 'error';
        case 'SUBMITTED':
            return 'info';
        default:
            return 'default';
    }
}

/**
 * Close obligation 3 (advance_service_case_phase): a SUBMITTED labour claim refuses the close. A
 * MANUFACTURER-billed visit's claim is SUBMITTED from WORK_COMPLETE until the job is PAID
 * (20261119500000): the visit is over, but the case waits for the manufacturer's payment.
 */
export const LABOUR_CLAIM_PENDING_CLOSE_MESSAGE =
    "The visit is done; the case can close once the manufacturer's payment for it goes through.";

/**
 * Plain words for the close refusals the close dialog explains: 22023 from
 * advance_service_case_phase (close_registration_service_case closes through it, so both close RPCs
 * raise the same text) for obligation 3 (labour claim SUBMITTED) and obligation 4 (service visit
 * under way, 20261120200000). Null for anything else, which the caller shows as the server wrote it.
 */
export function closeRefusalMessage(error: { code?: string; message?: string } | null): string | null {
    if (!error?.message || (error.code && error.code !== '22023')) return null;
    if (error.message.includes('labour_claim_status is still SUBMITTED')) return LABOUR_CLAIM_PENDING_CLOSE_MESSAGE;
    const visit = /Finish or cancel the service visit \(Job #(\w+), still ([^)]+)\) before closing this case/.exec(
        error.message,
    );
    if (visit) {
        return `The service visit (Job #${visit[1]}) is still ${visit[2]}. A case cannot be closed while its service visit is under way: finish the visit, or cancel the job if it is no longer needed, then close the case.`;
    }
    return null;
}

export function getAnchorKind(c: Pick<ServiceCaseRow, 'registration_id'>): ServiceCaseAnchorKind {
    return c.registration_id ? 'registration' : 'job';
}

/** The phase the case may move to next (one step at a time; CLOSED needs an outcome). */
export function nextPhase(phase: ServiceCasePhase): ServiceCasePhase | null {
    const idx = SERVICE_CASE_PHASES.indexOf(phase);
    if (idx < 0 || idx >= SERVICE_CASE_PHASES.length - 1) return null;
    return SERVICE_CASE_PHASES[idx + 1];
}

export function formatDateTime(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export function formatDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function registrationModelLabel(
    reg: ServiceCaseRegistrationEmbed | null | undefined,
): string {
    if (!reg) return '—';
    return reg.manufacturer_models?.model_code || reg.model_text || '—';
}

export function siteLabel(parts: Array<string | null | undefined>): string {
    return parts.filter(Boolean).join(', ') || '—';
}

export type ServiceCaseActionKey =
    | 'advance'
    | 'close'
    | 'note'
    | 'coverage'
    | 'rma'
    | 'shipped'
    | 'return'
    | 'labour'
    | 'reopen'
    | 'hazard';

export const ADMIN_SERVICE_CASE_ACTIONS: readonly ServiceCaseActionKey[] = [
    'advance',
    'close',
    'note',
    'coverage',
    'rma',
    'shipped',
    'return',
    'labour',
    'reopen',
    'hazard',
];

/** Manufacturer members with `client:approve` on registration-anchored cases. */
export const MANUFACTURER_SERVICE_CASE_ACTIONS: readonly ServiceCaseActionKey[] = [
    'coverage',
    'rma',
    'shipped',
    'return',
    'note',
    'close',
];

/** Outcomes that swap the unit's serial (`close_registration_service_case` needs `p_new_serial`). */
export const REPLACEMENT_OUTCOMES: readonly ServiceCaseOutcome[] = ['REPLACED_UNDER_WARRANTY', 'REPLACED_PAID'];

export function isReplacementOutcome(outcome: string | null | undefined): boolean {
    return REPLACEMENT_OUTCOMES.includes(outcome as ServiceCaseOutcome);
}

export function hasRecordedRma(c: Pick<ServiceCaseRow, 'rma_reference' | 'rma_issued_at'>): boolean {
    return Boolean(c.rma_reference || c.rma_issued_at);
}

// ---------------------------------------------------------------------------
// Manufacturer service dispatch (phase 3b): catalogue items, dispatch, billing
// ---------------------------------------------------------------------------

export type ServiceItemBilledTo = 'MANUFACTURER' | 'HOMEOWNER';
export type ServiceItemAppliesWhen = 'IN_WARRANTY' | 'OUT_OF_WARRANTY' | 'ANY';
export type ServiceDispatchMode = 'ACCREDITED_FIRST' | 'OPEN_MARKET' | 'DIRECT';
export type ManufacturerDefaultDispatchMode = Exclude<ServiceDispatchMode, 'DIRECT'>;
export type ReplacementShipTo = 'TRADE' | 'HOMEOWNER';

export const SERVICE_ITEM_BILLED_TO: readonly ServiceItemBilledTo[] = ['MANUFACTURER', 'HOMEOWNER'];
export const SERVICE_ITEM_APPLIES_WHEN: readonly ServiceItemAppliesWhen[] = ['IN_WARRANTY', 'OUT_OF_WARRANTY', 'ANY'];
export const SERVICE_DISPATCH_MODES: readonly ServiceDispatchMode[] = ['ACCREDITED_FIRST', 'OPEN_MARKET', 'DIRECT'];
export const MANUFACTURER_DEFAULT_DISPATCH_MODES: readonly ManufacturerDefaultDispatchMode[] = [
    'ACCREDITED_FIRST',
    'OPEN_MARKET',
];
export const REPLACEMENT_SHIP_TO: readonly ReplacementShipTo[] = ['TRADE', 'HOMEOWNER'];

export const BILLED_TO_LABELS: Record<ServiceItemBilledTo, string> = {
    MANUFACTURER: 'Manufacturer',
    HOMEOWNER: 'Homeowner (pays on site)',
};

export const APPLIES_WHEN_LABELS: Record<ServiceItemAppliesWhen, string> = {
    IN_WARRANTY: 'In warranty (covered / goodwill)',
    OUT_OF_WARRANTY: 'Out of warranty (not covered)',
    ANY: 'Any coverage',
};

export const DISPATCH_MODE_LABELS: Record<ServiceDispatchMode, string> = {
    ACCREDITED_FIRST: 'Accredited network first',
    OPEN_MARKET: 'Open market',
    DIRECT: 'Direct to one accredited trade',
};

export const SHIP_TO_LABELS: Record<ReplacementShipTo, string> = {
    TRADE: 'Trade',
    HOMEOWNER: 'Homeowner',
};

/** `labelFor(BILLED_TO_LABELS, value)`: the label, or the raw value for anything unexpected. */
export function labelFor<K extends string>(labels: Record<K, string>, value: string | null | undefined): string {
    if (!value) return '—';
    return labels[value as K] ?? value;
}

/**
 * Whether a catalogue item may be dispatched for a case's coverage decision — the same rule
 * `spawn_service_visit` enforces: COVERED / GOODWILL take IN_WARRANTY or ANY items billed to the
 * manufacturer (in-warranty and goodwill visits are the manufacturer's to pay, so a HOMEOWNER item
 * is never offered on them), NOT_COVERED takes OUT_OF_WARRANTY or ANY items billed to either, and
 * nothing is dispatched while coverage is PENDING.
 */
export function serviceItemMatchesCoverage(
    appliesWhen: string,
    coverageDecision: string,
    billedTo: string,
): boolean {
    if (coverageDecision === 'COVERED' || coverageDecision === 'GOODWILL') {
        if (billedTo === 'HOMEOWNER') return false;
        return appliesWhen === 'IN_WARRANTY' || appliesWhen === 'ANY';
    }
    if (coverageDecision === 'NOT_COVERED') {
        return appliesWhen === 'OUT_OF_WARRANTY' || appliesWhen === 'ANY';
    }
    return false;
}

/** Phases in which `spawn_service_visit` accepts a booking. */
export const SERVICE_VISIT_PHASES: readonly ServiceCasePhase[] = ['TRIAGE', 'ATTENDANCE'];
